/* eslint-disable
   no-restricted-imports,
   react-hooks/immutability,
   react-hooks/exhaustive-deps
*/
/**
 * This is the starting point of your application.
 * oneAudi OS and Feature Hub Apps will use this file to bootstrap the app.
 */
import React, { useContext, useEffect, useState, useRef } from 'react';
import { ThemeProvider } from '@oneaudi/unified-web-common';
import type { Logger } from '@feature-hub/core';
import type { TrackingServiceV2 } from '@oneaudi/audi-tracking-service/dist/v2/types';
import type { LocaleServiceV1 } from '@volkswagen-onehub/locale-service';
import type { EditorJsonContent } from './contexts/ContentInterfaces';
import { validateConfig } from '../helper/validateConfig';
import { ContentContext } from './contexts';
import mockContent from './content.json';
import FeatureGallery from './components/feature-gallery-carousel';
import { useInViewEffect } from '@oneaudi/feature-app-utils';
import { getThemeName } from '../alpha-helper';
import { useWltpComplianceGate } from './useWltpComplianceGate';
import { fetchCaeEnforcement } from './marketCompliance';
import type { GfaServiceConfigProviderV1 } from '@volkswagen-onehub/gfa-service-config-provider';

export interface FeatureAppProps {
  trackingService?: TrackingServiceV2;
  loggerService?: Logger;
  config?: EditorJsonContent;
  environment: string;
  localeService: LocaleServiceV1;
  featureAppId: string;
  isMarketComplianceEnabled?: boolean;
  initialWltpCompliance?: boolean[];
  gfaServiceConfigProvider?: GfaServiceConfigProviderV1;
  logger?: Pick<Logger, 'warn' | 'error'>;
  initialWltpLogs?: string[];
}

const FeatureApp: React.FC<FeatureAppProps> = ({ config, ...inherited }: FeatureAppProps) => {
  const {
    localeService,
    isMarketComplianceEnabled,
    initialWltpCompliance,
    gfaServiceConfigProvider,
    logger,
    initialWltpLogs,
    featureAppId,
  } = inherited;

  const context = useContext(ContentContext);
  const hasReplayedInitialWltpLogs = useRef(false);

  // `isMarketComplianceEnabled` is provided by SSR pre-check; on the client we
  // fetch the ACS flag asynchronously via the gfa service config provider.
  const [complianceEnabled, setComplianceEnabled] = useState<boolean>(
    isMarketComplianceEnabled ?? false,
  );

  useEffect(() => {
    if (hasReplayedInitialWltpLogs.current) return;
    if (!Array.isArray(initialWltpLogs) || !initialWltpLogs.length) return;

    initialWltpLogs.forEach((log) => {
      console.error(log);
    });
    console.error('Wltp Compliance per silde', initialWltpCompliance);
    hasReplayedInitialWltpLogs.current = true;
  }, [initialWltpLogs]);

  useEffect(() => {
    if (isMarketComplianceEnabled !== undefined) return undefined;
    if (hasReplayedInitialWltpLogs.current) return;

    let cancelled = false;
    fetchCaeEnforcement({ gfaServiceConfigProvider })
      .then((result) => {
        if (!cancelled) setComplianceEnabled(result.caeDataRequired);
      })
      .catch(() => {
        // Error fetching CAE enforcement
      });
    return () => {
      cancelled = true;
    };
  }, [gfaServiceConfigProvider, isMarketComplianceEnabled]);

  const compliancePerSlide = useWltpComplianceGate({
    isMarketComplianceEnabled: isMarketComplianceEnabled || complianceEnabled,
    initialCompliance: initialWltpCompliance,
    logger,
  });

  let trackingService = {} as TrackingServiceV2;

  if (typeof window !== 'undefined' && inherited.trackingService) {
    trackingService = inherited.trackingService;
  }

  useEffect(() => {
    trackingService.featureAppName = __FEATURE_APP_NAME__;
    const readyEvent = {
      event: {
        attributes: {
          componentName: __FEATURE_APP_NAME__,
          elementName: '',
          label: '',
          clickID: '',
          pos: '',
          targetURL: '',
          value: '',
        },
        eventInfo: {
          eventAction: 'feature_app_ready',
          eventName: `feature gallery - feature app ready`,
        },
      },
    };
    trackingService.track(readyEvent);
  }, []);

  useInViewEffect(() => {
    const dataLayerEvent = {
      eventInfo: {
        eventAction: 'impression',
        eventName: 'feature gallery - impression',
      },
      attributes: {
        componentName: 'feature-gallery',
        label: '',
        targetURL: '',
        clickID: '',
        elementName: '',
        value: '',
        pos: '',
      },
    };
    trackingService.track({ event: dataLayerEvent });
  });

  const configToUse = validateConfig(config) ? config : context?.content || mockContent;
  const baseContent = configToUse as EditorJsonContent;

  // Filter gallery based on per-slide compliance
  const filteredGallery =
    compliancePerSlide !== undefined
      ? baseContent.gallery.filter((_, index: number) => compliancePerSlide[index])
      : baseContent.gallery;

  // Hide entire FA only if all slides were filtered out
  if (filteredGallery.length === 0) {
    return null;
  }

  return (
    <ThemeProvider
      theme={{
        name: getThemeName(baseContent.theme === 'darkTheme' ? 'dark' : 'light'),
      }}
    >
      <FeatureGallery
        {...baseContent}
        gallery={filteredGallery}
        trackingService={trackingService}
        localeService={localeService}
        featureAppId={featureAppId}
      />
    </ThemeProvider>
  );
};
export default FeatureApp;
